/**
 * AdoptionData — data provider for the Admin → Adoption analytics feature.
 *
 * Source of truth is the live backend, not the standalone SPA's S3 snapshots:
 *   - events:           GET  /api/usage/fetch?days&include_prompts=true&raw_events=true
 *                       returns per-event rows (user, timestamp, prompt text, cell).
 *                       An empty result is valid — it means no matching external
 *                       usage in the selected window (the query excludes internal
 *                       @plexresearch.com/@gmail.com users), not a backend failure.
 *   - classifications:  POST /api/usage/categorize  (Bedrock-backed, cached).
 *
 * Exposes the same shape the ported views expect: { events, classMap, aliases,
 * mergeMerck, ... } plus company-selection state for the Companies drill-down.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { RequestBuilder } from '../../../queries/request';
import { getConfig } from '../../../lib/configMgr';
import { normalizeEvents, DEFAULT_COMPANY_ALIASES } from '../../../lib/adoptionAnalytics';

const { ExternalRESTAPI } = getConfig();

// Kept for reference (matches the ingest filter applied server-side in usage_fetch).
export const GRAYLOG_QUERY =
  'source:rest-api* AND _exists_:user AND NOT user:anonymous AND NOT user:system ' +
  'AND NOT user:/@.*plexresearch\\.com/ AND NOT user:/@gmail.com/ ' +
  'AND (_exists_:messages OR _exists_:initial_prompt)';

const Ctx = createContext(null);
export const useData = () => useContext(Ctx);

export async function classifyWithPolling(texts, fetchJson, opts = {}) {
  const { intervalMs = 2000, onProgress = () => {}, maxPolls = 150 } = opts;
  const prompts = texts.map((t, i) => ({ id: i, text: String(t).slice(0, 600) }));
  let res = await fetchJson({ prompts });
  let polls = 0;
  while (res && res.status === 'running' && polls < maxPolls) {
    onProgress(res.progress || 0);
    if (intervalMs) await new Promise((r) => setTimeout(r, intervalMs));
    res = await fetchJson({ job_id: res.job_id });
    polls += 1;
  }
  const map = {};
  (res && res.results || []).forEach((r) => {
    const t = texts[r.id];
    if (t != null) map[t] = { areas: r.areas || [], uses: r.uses || [] };
  });
  return map;
}

async function fetchEvents(authData, days) {
  const url = new URL(ExternalRESTAPI.UsageFetch, window.location.origin);
  url.searchParams.set('days', String(days));
  url.searchParams.set('include_prompts', 'true');
  url.searchParams.set('raw_events', 'true');
  const resp = await new RequestBuilder(url).withAuthorization(authData).fetch();
  const data = await resp.json();
  return normalizeEvents(data);
}

async function classify(authData, texts, onProgress) {
  if (!ExternalRESTAPI.UsageCategorize) return {};
  const url = new URL(ExternalRESTAPI.UsageCategorize, window.location.origin);
  const fetchJson = async (body) => {
    const resp = await new RequestBuilder(url)
      .withAuthorization(authData)
      .withJSONContentType()
      .asPOST(JSON.stringify(body))
      .fetch();
    if (!resp.ok) { const e = new Error(`categorize ${resp.status}`); e.code = resp.status; throw e; }
    return resp.json();
  };
  return classifyWithPolling(texts, fetchJson, { onProgress });
}

export function AdoptionDataProvider({ authData, children }) {
  const [days, setDays] = useState(30);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mergeMerck, setMergeMerck] = useState(true);
  const [classMap, setClassMap] = useState({});
  const [classState, setClassState] = useState('idle'); // idle|loading|done|unconfigured|error
  const [classProgress, setClassProgress] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  // PLEX-4035: let callers re-trigger the events fetch after a transient failure
  // (e.g. a "Failed to fetch"/ServerOffline network blip) without a full page reload.
  const [reloadNonce, setReloadNonce] = useState(0);
  const reload = () => setReloadNonce((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    fetchEvents(authData, days)
      .then((ev) => { if (!cancelled) { setEvents(ev); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [authData, days, reloadNonce]);

  useEffect(() => {
    if (!events.length) { setClassMap({}); setClassState('idle'); setClassProgress(0); return; }
    let cancelled = false;
    const texts = [...new Set(events.map((e) => e.text))];
    setClassState('loading'); setClassProgress(0);
    classify(authData, texts, (p) => { if (!cancelled) setClassProgress(p); })
      .then((m) => { if (!cancelled) { setClassMap(m); setClassState('done'); setClassProgress(1); } })
      .catch((e) => { if (!cancelled) { setClassMap({}); setClassState(e.code === 404 ? 'unconfigured' : 'error'); setClassProgress(0); } });
    return () => { cancelled = true; };
  }, [authData, events]);

  const aliases = useMemo(() => (mergeMerck ? DEFAULT_COMPANY_ALIASES : {}), [mergeMerck]);

  const value = {
    loading, error, events, classMap, classState, classProgress, aliases,
    mergeMerck, setMergeMerck, days, setDays,
    selectedCompany, setSelectedCompany, reload,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
